import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, NetworkError, api, apiFile, enc, saveBlob, webImageUrl } from '../api';
import { useAuth } from '../auth';
import { ScanField } from '../components/ScanField';
import { ErrorBox, Field, Loading, Success, Thumb, fmtDate } from '../components/ui';
import { Icon } from '../components/Icon';
import {
  OUTCOME_LABEL,
  breakup,
  type FixLine,
  type ManifestImport,
  type ManifestReading,
  type Outcome,
  type ReceiptDetail,
  type ReceiptSummary,
  type ReceiveEvent,
  type ScanResult,
} from '../receiving';

/** POST a file as multipart (same headers as api()). */
async function uploadFile<T>(path: string, field: string, file: File): Promise<T> {
  const form = new FormData();
  form.append(field, file);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: 'POST', headers: { 'X-Requested-With': 'mal-app' }, body: form, credentials: 'same-origin' });
  } catch {
    throw new NetworkError();
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body?.error?.code ?? 'error', body?.error?.message ?? `Request failed (${res.status})`, body?.error?.details);
  return body as T;
}

// ------------------------------------------------------------------ list
export function ReceivingListPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState('open');
  const q = useQuery({ queryKey: ['receipts', status], queryFn: () => api<{ receipts: ReceiptSummary[] }>(`/receipts?status=${status}`).then((r) => r.receipts) });
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string }>('/receipts', { body: { receiptNumber: number.trim() } });
      nav(`/receiving/${r.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const imports = useQuery({
    queryKey: ['manifest-imports'],
    queryFn: () => api<{ imports: ManifestImport[] }>('/manifest-imports').then((r) => r.imports),
  });
  const importFile = async (input: HTMLInputElement) => {
    const file = input.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      // A scanned PDF is read in the background (202); the manifest reader's items.csv is imported at once.
      const r = await uploadFile<{ importId?: string; created?: { id: string; receiptNumber: string; lines: number; units: number }[] }>('/receipts/import', 'file', file);
      if (r.importId) await imports.refetch();
      else setMsg(r.created!.map((c) => `Manifest ${c.receiptNumber}: ${c.lines} items, ${c.units} units`).join(' · '));
      input.value = '';
      await q.refetch();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="narrow">
      <h1>Receiving</h1>
      <p className="muted small">Each manifest is processed piece by piece into retail, auction or garbage.</p>
      <div className="filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="open">Open manifests</option>
          <option value="closed">Closed</option>
          <option value="all">All</option>
        </select>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {imports.data?.map((m) => (
        <ManifestImportCard
          key={m.id}
          id={m.id}
          onDone={() => {
            void imports.refetch();
            void q.refetch();
          }}
        />
      ))}
      {q.data?.length === 0 && <p className="muted">No manifests yet. Upload the scanned manifest PDF below, or start one by hand.</p>}
      <ul className="receipt-list">
        {q.data?.map((r) => (
          <li key={r.id}>
            <Link to={`/receiving/${r.id}`} className="card receipt-card">
              <div className="row between">
                <strong className="mono">#{r.receiptNumber}</strong>
                <span className={`badge ${r.status === 'open' ? 'badge-info' : ''}`}>{r.status}</span>
              </div>
              <div className="progress">
                <div style={{ width: `${r.expected ? Math.min(100, (r.processed / r.expected) * 100) : 0}%` }} />
              </div>
              <div className="muted small">
                {r.processed} of {r.expected || '?'} units · {r.source === 'ocr' ? 'from the manifest reader' : 'started by hand'} · {r.receivedOn}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <section className="card">
        <h2>New manifest</h2>
        <Field label="Upload the scanned manifest (PDF, scanned in colour, up to 300 MB)">
          <input type="file" accept=".pdf,application/pdf,.csv,text/csv" disabled={busy} onChange={(e) => importFile(e.target)} />
        </Field>
        {busy && <p className="muted small">Uploading…</p>}
        <div className="row gap">
          <Field label="…or start by hand: manifest number">
            <input value={number} onChange={(e) => setNumber(e.target.value)} inputMode="numeric" placeholder="e.g. 13667" />
          </Field>
          <button className="btn" disabled={busy || !number.trim()} onClick={create}>
            Start
          </button>
        </div>
        {msg && <Success>{msg}</Success>}
        <ErrorBox error={error} />
      </section>
    </div>
  );
}

/** An uploaded manifest PDF: reading progress, then what was found, then Create manifest. */
function ManifestImportCard({ id, onDone }: { id: string; onDone: () => void }) {
  const nav = useNavigate();
  const q = useQuery({
    queryKey: ['manifest-import', id],
    queryFn: () => api<{ import: ManifestImport; result: ManifestReading | null }>(`/manifest-imports/${id}`),
    refetchInterval: (query) => (query.state.data?.import.status === 'reading' ? 3000 : false),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!q.data) return <ErrorBox error={q.error} />;
  const { import: m, result } = q.data;
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ created: { id: string }[] }>(`/manifest-imports/${id}/create`, { body: {} });
      onDone();
      if (r.created.length === 1) nav(`/receiving/${r.created[0].id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/manifest-imports/${id}/dismiss`, { body: {} });
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const fresh = result?.receipts.filter((r) => !r.exists) ?? [];
  return (
    <section className="card import-card">
      <div className="row between">
        <strong>{m.fileName}</strong>
        <span className={`badge ${m.status === 'failed' ? 'badge-warn' : 'badge-info'}`}>{m.status === 'reading' ? 'reading…' : m.status}</span>
      </div>
      {m.status === 'reading' && (
        <>
          <div className="progress">
            <div style={{ width: `${m.pagesTotal ? (m.pagesDone / m.pagesTotal) * 100 : 2}%` }} />
          </div>
          <p className="muted small">
            {m.pagesTotal ? `${m.pagesDone} of ${m.pagesTotal} pages read` : 'Starting…'} · about 10–20 seconds a page. You can leave this page; it keeps reading.
          </p>
        </>
      )}
      {m.status === 'failed' && <p className="error-text small">{m.error}</p>}
      {m.status !== 'reading' && (
        <div className="row">
          <button className="btn btn-small" disabled={busy} onClick={dismiss}>
            Dismiss
          </button>
        </div>
      )}
      {m.status === 'failed' && <ErrorBox error={error} />}
      {result && (
        <>
          {result.blackAndWhite.length > 0 && (
            <p className="warn-text small">
              {result.blackAndWhite.length} page(s) were scanned in black and white. Re-scan in colour if lines are missing or wrong.
            </p>
          )}
          {result.receipts.length === 0 && <p className="warn-text small">No manifest pages were found in this PDF.</p>}
          {result.receipts.map((r) => {
            const fixes = r.fixes ?? [];
            const units = r.items.reduce((t, i) => t + Math.round(i.qty), 0);
            const toFix = fixes.filter((f) => !f.resolution).length;
            // Lines read + lines fixed by hand, against the printed total.
            const cents = (s: string) => Math.round(Number(s.replace(/,/g, '')) * 100);
            const fixedCents = fixes.reduce((t, f) => t + (f.resolution?.action === 'fixed' ? Math.round(f.resolution.qty * Number(f.resolution.sell) * 100) : 0), 0);
            const sumCents = cents(r.totalSellFromLines) + fixedCents;
            const printed = r.totalSellPrinted ? cents(r.totalSellPrinted) : null;
            const fmt = (c: number) => (c / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return (
              <div key={r.receipt} className="import-receipt">
                <div className="row between">
                  <strong className="mono">#{r.receipt}</strong>
                  <span className="muted small">{r.date ?? ''}</span>
                </div>
                <div className="small">
                  {r.items.length} items · {units} units · {r.pagesRead} pages
                  {r.exists && <span className="badge"> already a manifest</span>}
                </div>
                {r.missingPages.length > 0 && <div className="warn-text small">Missing page(s): {r.missingPages.join(', ')}. Scan them and upload again.</div>}
                {!r.lastPageFound && <div className="warn-text small">The last page (with the totals) was not found.</div>}
                {printed !== null &&
                  (sumCents === printed ? (
                    <div className="ok-text small"><Icon name="check" size={15} /> Lines add up to the printed total (${fmt(printed)}).</div>
                  ) : (
                    <div className="warn-text small">
                      Lines add up to ${fmt(sumCents)}; the printed total is ${fmt(printed)} ({sumCents < printed ? `$${fmt(printed - sumCents)} short` : `$${fmt(sumCents - printed)} over`}).
                    </div>
                  ))}
                {fixes.length > 0 && !r.exists && (
                  <div className="fix-section">
                    <div className={toFix ? 'warn-text small' : 'ok-text small'}>
                      <strong>
                        {toFix
                          ? `${toFix} line${toFix === 1 ? '' : 's'} need${toFix === 1 ? 's' : ''} you: compare with the scan and type what is printed, or skip.`
                          : `All ${fixes.length} flagged lines dealt with.`}
                      </strong>
                    </div>
                    {fixes.map((f) => (
                      <FixRow key={f.key} importId={id} receipt={r.receipt} fix={f} rates={r.salvageRates ?? []} editable={m.status === 'ready'} onSaved={() => q.refetch()} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {m.status === 'ready' && fresh.length > 0 && (() => {
            const left = fresh.reduce((t, r) => t + (r.fixes ?? []).filter((f) => !f.resolution).length, 0);
            return (
              <>
                <button className="btn btn-primary" disabled={busy || left > 0} onClick={create}>
                  {fresh.length === 1 ? `Create manifest #${fresh[0].receipt}` : `Create ${fresh.length} manifests`}
                </button>
                {left > 0 && <p className="muted small">Fix or skip the {left} flagged line(s) first.</p>}
              </>
            );
          })()}
          <ErrorBox error={error} />
        </>
      )}
    </section>
  );
}

/** One line the reader could not confirm: the cut-out from the scan, and the values as printed. */
function FixRow(props: { importId: string; receipt: string; fix: FixLine; rates: number[]; editable: boolean; onSaved: () => void }) {
  const { importId, receipt, fix, rates, editable, onSaved } = props;
  const res = fix.resolution;
  const s = fix.suggested;
  const [editing, setEditing] = useState(!res);
  const [item, setItem] = useState(res?.action === 'fixed' ? res.item : (s.item ?? ''));
  const [qty, setQty] = useState(res?.action === 'fixed' ? String(res.qty) : String(Math.round(Number(s.qty ?? '1')) || 1));
  const [sell, setSell] = useState(res?.action === 'fixed' ? res.sell : (s.sell ?? '').replace(/,/g, ''));
  const [pct, setPct] = useState(res?.action === 'fixed' ? String(res.salvagePct) : (s.salvagePct && rates.includes(Number(s.salvagePct)) ? s.salvagePct : String(rates[0] ?? '')));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const save = async (action: 'fixed' | 'skipped' | 'reopen') => {
    setBusy(true);
    setError(null);
    try {
      await api(`/manifest-imports/${importId}/fixes`, {
        method: 'PUT',
        body: { receipt, key: fix.key, action, ...(action === 'fixed' ? { item: item.trim(), description: s.description ?? undefined, qty: Number(qty), sell: sell.trim(), salvagePct: Number(pct) } : {}) },
      });
      setEditing(action === 'reopen');
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const ext = Number(qty) * Number(sell);
  return (
    <div className={`fix-row ${res ? 'fix-done' : ''}`}>
      <div className="row between small">
        <span>
          Page {fix.page} · {fix.kind === 'unreadable' ? 'could not be read' : fix.problems.join('; ')}
        </span>
        {res && (
          <span className={res.action === 'fixed' ? 'ok-text' : 'muted'}>
            {res.action === 'fixed' ? <><Icon name="check" size={15} /> fixed: {res.item} × {res.qty} @ {res.sell}</> : 'skipped'}
          </span>
        )}
      </div>
      {fix.cropKey ? (
        <a className="fix-crop-wrap" href={`/api/manifest-imports/${importId}/fixes/${encodeURIComponent(receipt)}/${fix.key}/image`} target="_blank" rel="noreferrer" title="Open full size">
          <img className="fix-crop" src={`/api/manifest-imports/${importId}/fixes/${encodeURIComponent(receipt)}/${fix.key}/image`} alt={`Scan of the line: ${fix.raw}`} loading="lazy" />
        </a>
      ) : (
        <div className="mono small fix-raw">{fix.raw}</div>
      )}
      <div className="muted small">
        Read as: <span className="mono">{fix.raw}</span>
      </div>
      {editable && editing && (
        <>
          <div className="fix-fields">
            <Field label="Item #">
              <input value={item} onChange={(e) => setItem(e.target.value)} inputMode="numeric" className="mono" />
            </Field>
            <Field label="Qty">
              <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="numeric" />
            </Field>
            <Field label="Sell price">
              <input value={sell} onChange={(e) => setSell(e.target.value)} inputMode="decimal" placeholder="19.99" />
            </Field>
            <Field label="Salvage %">
              <input value={pct} onChange={(e) => setPct(e.target.value)} inputMode="numeric" list={`rates-${receipt}`} />
            </Field>
          </div>
          <datalist id={`rates-${receipt}`}>
            {rates.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          {ext > 0 && Number(pct) >= 0 && (
            <div className="muted small">
              Ext. sell {ext.toFixed(2)} · salvage {((Math.round(ext * 100) * Number(pct)) / 10000).toFixed(2)}: should match the scan.
            </div>
          )}
          <ErrorBox error={error} />
          <div className="row gap">
            <button className="btn btn-small btn-primary" disabled={busy || !item.trim() || !qty || !sell.trim() || pct === ''} onClick={() => save('fixed')}>
              Save
            </button>
            <button className="btn btn-small" disabled={busy} onClick={() => save('skipped')} title="Leave this line out of the manifest">
              Skip (not an item)
            </button>
          </div>
        </>
      )}
      {editable && !editing && (
        <button className="link small" disabled={busy} onClick={() => save('reopen')}>
          Change
        </button>
      )}
    </div>
  );
}

// --------------------------------------------------------------- one manifest
export function ReceiptPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<'receive' | 'report' | 'tolist'>('receive');
  const q = useQuery({ queryKey: ['receipt', id], queryFn: () => api<ReceiptDetail>(`/receipts/${id}`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const { receipt, report } = q.data!;
  const t = report.totals;
  return (
    <div className="narrow">
      <div className="row between">
        <h1>
          Manifest <span className="mono">#{receipt.receiptNumber}</span>
        </h1>
        <span className={`badge ${receipt.status === 'open' ? 'badge-info' : ''}`}>{receipt.status}</span>
      </div>
      <div className="progress big">
        <div style={{ width: `${t.progressPct}%` }} />
      </div>
      <p className="small">
        <strong>{t.processed}</strong> of {t.expected} units processed ({t.progressPct}%) · retail {t.unitPct.retail}% · auction {t.unitPct.auction}% · garbage{' '}
        {t.unitPct.garbage}%
      </p>
      <div className="seg">
        <button className={`btn btn-small ${tab === 'receive' ? 'on' : ''}`} onClick={() => setTab('receive')} disabled={receipt.status !== 'open'}>
          Receive
        </button>
        <button className={`btn btn-small ${tab === 'report' ? 'on' : ''}`} onClick={() => setTab('report')}>
          Report
        </button>
        <button className={`btn btn-small ${tab === 'tolist' ? 'on' : ''}`} onClick={() => setTab('tolist')}>
          To list ({Math.max(0, Math.round(t.auction - t.lotsListed))})
        </button>
      </div>
      {tab === 'receive' && receipt.status === 'open' && <ReceivePanel receiptId={receipt.id} onChange={() => q.refetch()} />}
      {(tab === 'report' || receipt.status !== 'open') && tab !== 'tolist' && <ReportPanel detail={q.data!} onChange={() => q.refetch()} />}
      {tab === 'tolist' && <ToListPanel receiptId={receipt.id} />}
    </div>
  );
}

// ------------------------------------------------------------------ receive
function ReceivePanel({ receiptId, onChange }: { receiptId: string; onChange: () => void }) {
  const qc = useQueryClient();
  const binKey = `mal.receiveBin.${receiptId}`;
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('');
  const [bin, setBin] = useState<{ id: string; code: string } | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(binKey) ?? 'null');
    } catch {
      return null;
    }
  });
  const [breaking, setBreaking] = useState(false);
  const [units, setUnits] = useState('');
  const [packs, setPacks] = useState<{ size: number; count: number }[]>([]);
  const [typedPrice, setTypedPrice] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [photoMatches, setPhotoMatches] = useState<{ id: string; itemNumber: string; title: string | null; photoId: string | null }[] | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const recent = useQuery({ queryKey: ['receive-events', receiptId], queryFn: () => api<{ events: ReceiveEvent[] }>(`/receipts/${receiptId}/events?mine=1`).then((r) => r.events) });

  useEffect(() => {
    try {
      localStorage.setItem(binKey, JSON.stringify(bin));
    } catch {
      /* private mode: just not remembered */
    }
  }, [bin, binKey]);

  const reset = () => {
    setOutcome(null);
    setQty('1');
    setReason('');
    setBreaking(false);
    setUnits('');
    setPacks([]);
    setTypedPrice('');
    setPhotoMatches(null);
  };
  const lookup = async (code: string) => {
    setScanError(null);
    setMsg(null);
    setError(null);
    reset();
    try {
      setScan(await api<ScanResult>(`/receipts/${receiptId}/scan/${enc(code)}`));
    } catch (e) {
      setScan(null);
      setScanError(e);
    }
  };
  const readPhoto = async (file: File) => {
    setBusy(true);
    setScanError(null);
    try {
      const r = await uploadFile<{ numbers: string[]; matches: { id: string; itemNumber: string; title: string | null; photoId: string | null }[] }>('/receiving/read-label', 'photo', file);
      if (!r.matches.length) setScanError(new Error(r.numbers.length ? `Read ${r.numbers.join(', ')} but none is a known item number. Try again closer, or type it.` : 'No item number could be read. Try again closer, in good light.'));
      setPhotoMatches(r.matches);
    } catch (e) {
      setScanError(e);
    } finally {
      setBusy(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };
  const scanBin = async (code: string) => {
    setError(null);
    try {
      const b = (await api<{ bin: { id: string; code: string; purpose: string; active: boolean } }>(`/bins/by-code/${enc(code)}`)).bin;
      if (b.purpose !== 'retail') throw new Error(`Bin ${b.code} is an auction bin. Scan a retail bin.`);
      setBin({ id: b.id, code: b.code });
    } catch (e) {
      setError(e);
    }
  };

  const afterSave = async (text: string) => {
    setMsg(text);
    reset();
    setScan(null);
    await qc.invalidateQueries({ queryKey: ['receive-events', receiptId] });
    onChange();
  };
  const post = (body: object) => api<{ event: ReceiveEvent }>(`/receipts/${receiptId}/events`, { body: { clientEventId: crypto.randomUUID(), ...body } });

  /** Prices exist before labels print: the item's (rule or typed), or each pack size's (from the rule, only if it has none yet). */
  const ensurePrice = async (packSize: number | null) => {
    if (!scan) return;
    if (packSize) {
      const have = await api<{ prices: { packSize: number | null }[] }>(`/products/${scan.product.id}/retail`);
      if (have.prices.some((p) => p.packSize === packSize)) return;
      await api(`/products/${scan.product.id}/retail`, { method: 'PUT', body: { packSize, basisUnits: scan.packs?.unitsPerItem ?? null, price: null } });
      return;
    }
    if (scan.price && !typedPrice.trim()) return;
    await api(`/products/${scan.product.id}/retail`, { method: 'PUT', body: { price: typedPrice.trim() || null, version: scan.price?.version ?? null } });
  };
  const printLabels = async (items: { productId: string; packSize: number | null; copies: number }[]) => {
    saveBlob(await apiFile('/retail/labels.pdf', { items }), `price-labels-${scan?.product.itemNumber ?? 'items'}.pdf`);
  };

  const save = async () => {
    if (!scan || !outcome) return;
    setBusy(true);
    setError(null);
    try {
      const pid = scan.product.id;
      if (outcome === 'retail') {
        if (!bin) throw new Error('Scan the retail bin first');
        if (breaking) {
          const use = packs.filter((p) => p.count > 0);
          if (!use.length) throw new Error('Enter how many units are on hand');
          for (const p of use) await ensurePrice(p.size);
          for (const p of use) await post({ productId: pid, outcome, quantity: p.count, binId: bin.id, packSize: p.size });
          await printLabels(use.map((p) => ({ productId: pid, packSize: p.size, copies: p.count })));
          await afterSave(`${use.map((p) => `${p.count} × ${p.size}-pack`).join(', ')} into ${bin.code}. Labels downloaded.`);
        } else {
          const n = Number(qty);
          await ensurePrice(null);
          await post({ productId: pid, outcome, quantity: n, binId: bin.id });
          await printLabels([{ productId: pid, packSize: null, copies: n }]);
          await afterSave(`${n} × ${scan.product.itemNumber} into ${bin.code}. Labels downloaded.`);
        }
      } else {
        const n = Number(qty);
        await post({ productId: pid, outcome, quantity: n, garbageReason: outcome === 'garbage' ? reason.trim() || undefined : undefined });
        await afterSave(`${n} × ${scan.product.itemNumber} → ${OUTCOME_LABEL[outcome]}.`);
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const needsPrice = outcome === 'retail' && !breaking && scan && !scan.price && !typedPrice.trim() && !scan.suggestion;
  const qtyOk = breaking || (Number.isInteger(Number(qty)) && Number(qty) > 0);

  return (
    <>
      <section className="card">
        <ScanField kind="product" onScan={lookup} busy={busy} autoFocus label="SCAN ITEM (barcode or item number)" />
        <button type="button" className="btn btn-small" disabled={busy} onClick={() => photoRef.current?.click()}>
          <Icon name="camera" /> Photo of the item number
        </button>
        <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => e.target.files?.[0] && readPhoto(e.target.files[0])} />
        {photoMatches && photoMatches.length > 0 && (
          <div className="mt">
            <div className="small">Is it one of these? Tap to confirm.</div>
            {photoMatches.map((m) => (
              <button key={m.id} className="btn photo-match" onClick={() => lookup(m.itemNumber)}>
                {m.photoId ? <img src={webImageUrl(m.photoId)} alt="" /> : <Thumb id={null} size={40} />}
                <span className="mono">{m.itemNumber}</span> {m.title}
              </button>
            ))}
          </div>
        )}
        <ErrorBox error={scanError} />
        {msg && <Success>{msg}</Success>}
      </section>

      {scan && (
        <section className="card">
          <div className="product-confirm">
            {scan.product.photoId ? <img src={webImageUrl(scan.product.photoId)} alt="" /> : <Thumb id={null} size={90} />}
            <div>
              <div className="mono">{scan.product.itemNumber}</div>
              <div>{scan.product.title ?? <span className="missing">no title</span>}</div>
              <div className="small muted">
                {scan.onManifest
                  ? `On this manifest: ${scan.onManifest.processed} of ${scan.onManifest.expected} done`
                  : 'Not on this manifest: it will show as an over.'}
              </div>
              <div className="small">
                {scan.price ? (
                  <>
                    Retail price <strong>${scan.price.price}</strong>
                  </>
                ) : scan.suggestion ? (
                  <>
                    Retail price will be <strong>${scan.suggestion.price}</strong> ({scan.suggestion.percent}% of Costco)
                  </>
                ) : (
                  <span className="field-error">No Costco price known: type a retail price before choosing Retail.</span>
                )}
              </div>
            </div>
          </div>
          <div className="seg outcome-seg">
            {(['retail', 'auction', 'garbage'] as Outcome[]).map((o) => (
              <button key={o} className={`btn ${outcome === o ? 'on' : ''}`} onClick={() => setOutcome(o)}>
                {OUTCOME_LABEL[o]}
              </button>
            ))}
          </div>
          {outcome && !breaking && (
            <Field label="How many">
              <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="numeric" style={{ width: '6rem' }} />
            </Field>
          )}
          {outcome === 'garbage' && (
            <Field label="Why (optional)">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. cracked, expired" />
            </Field>
          )}
          {outcome === 'retail' && (
            <>
              <div className="row gap">
                {bin ? (
                  <span className="bin-chip"><Icon name="bin" /> {bin.code}</span>
                ) : (
                  <span className="field-error small">Scan the retail bin these go into</span>
                )}
                {bin && (
                  <button className="btn btn-small btn-ghost" onClick={() => setBin(null)}>
                    Change bin
                  </button>
                )}
              </div>
              {!bin && <ScanField kind="bin" onScan={scanBin} label="RETAIL BIN" />}
              {!scan.price && (
                <Field label={scan.suggestion ? 'Price (leave blank to use the suggested price)' : 'Retail price (required)'}>
                  <input value={typedPrice} onChange={(e) => setTypedPrice(e.target.value)} inputMode="decimal" placeholder={scan.suggestion?.price ?? 'e.g. 19.99'} />
                </Field>
              )}
              <PackBreakup
                scan={scan}
                breaking={breaking}
                setBreaking={setBreaking}
                units={units}
                setUnits={setUnits}
                packs={packs}
                setPacks={setPacks}
                onPacksSaved={(p) => setScan({ ...scan, packs: p })}
              />
            </>
          )}
          <ErrorBox error={error} />
          {outcome && (
            <button className="btn btn-primary btn-big" disabled={busy || !qtyOk || !!needsPrice || (outcome === 'retail' && !bin)} onClick={save}>
              {busy ? 'Saving…' : outcome === 'retail' ? 'Save and print labels' : `Save as ${OUTCOME_LABEL[outcome]}`}
            </button>
          )}
        </section>
      )}

      <RecentScans receiptId={receiptId} events={recent.data ?? []} onChange={async () => (await recent.refetch(), onChange())} />
    </>
  );
}

function PackBreakup(props: {
  scan: ScanResult;
  breaking: boolean;
  setBreaking: (b: boolean) => void;
  units: string;
  setUnits: (s: string) => void;
  packs: { size: number; count: number }[];
  setPacks: (p: { size: number; count: number }[]) => void;
  onPacksSaved: (p: { unitsPerItem: number; sellSizes: number[] }) => void;
}) {
  const { scan, breaking, setBreaking, units, setUnits, packs, setPacks } = props;
  const [setup, setSetup] = useState(false);
  const [perItem, setPerItem] = useState(scan.packs ? String(scan.packs.unitsPerItem) : '');
  const [sizes, setSizes] = useState(scan.packs ? scan.packs.sellSizes.join(', ') : '');
  const [error, setError] = useState<unknown>(null);
  const p = scan.packs;
  const n = Number(units);
  const left = breaking && p ? n - packs.reduce((t, x) => t + x.size * x.count, 0) : 0;

  const savePacks = async () => {
    setError(null);
    try {
      const sellSizes = sizes.split(/[,\s]+/).map(Number).filter((x) => Number.isInteger(x) && x > 0);
      const r = await api<{ packs: { unitsPerItem: number; sellSizes: number[] } }>(`/products/${scan.product.id}/pack-sizes`, {
        method: 'PUT',
        body: { unitsPerItem: Number(perItem), sellSizes },
      });
      props.onPacksSaved(r.packs);
      setSetup(false);
    } catch (e) {
      setError(e);
    }
  };

  if (setup || (!p && breaking))
    return (
      <div className="tip">
        <strong>Pack sizes for this item</strong> (remembered for next time)
        <div className="grid2">
          <Field label="Units in one Costco item">
            <input value={perItem} onChange={(e) => setPerItem(e.target.value)} inputMode="numeric" placeholder="e.g. 28" />
          </Field>
          <Field label="Sizes we sell (comma-separated)">
            <input value={sizes} onChange={(e) => setSizes(e.target.value)} placeholder="e.g. 6, 4, 1" />
          </Field>
        </div>
        <ErrorBox error={error} />
        <div className="row gap">
          <button className="btn btn-small" onClick={savePacks}>
            Save pack sizes
          </button>
          <button className="btn btn-small btn-ghost" onClick={() => (setSetup(false), setBreaking(false))}>
            Cancel
          </button>
        </div>
      </div>
    );

  return (
    <div>
      <label className="check">
        <input type="checkbox" checked={breaking} onChange={(e) => setBreaking(e.target.checked)} /> Break up a multi-pack
        {p && <span className="muted small"> ({p.unitsPerItem}-pack → {p.sellSizes.join(' / ')})</span>}
      </label>
      {p && (
        <button className="btn btn-small btn-ghost" onClick={() => setSetup(true)}>
          Change pack sizes
        </button>
      )}
      {breaking && p && (
        <>
          <Field label="Units on hand">
            <input
              value={units}
              onChange={(e) => {
                setUnits(e.target.value);
                const u = Number(e.target.value);
                setPacks(u > 0 ? breakup(u, p.sellSizes).packs : []);
              }}
              inputMode="numeric"
              placeholder={`e.g. ${p.unitsPerItem}`}
              style={{ width: '6rem' }}
            />
          </Field>
          {packs.length > 0 && (
            <table className="table breakup">
              <tbody>
                {p.sellSizes.map((size) => {
                  const row = packs.find((x) => x.size === size) ?? { size, count: 0 };
                  return (
                    <tr key={size}>
                      <td>{size === 1 ? 'Singles' : `${size}-packs`}</td>
                      <td>
                        <input
                          value={row.count}
                          inputMode="numeric"
                          style={{ width: '4rem' }}
                          onChange={(e) => {
                            const c = Math.max(0, Number(e.target.value) || 0);
                            setPacks([...packs.filter((x) => x.size !== size), { size, count: c }].sort((a, b) => b.size - a.size));
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {packs.length > 0 && left !== 0 && (
            <div className={left > 0 ? 'field-error small' : 'field-error small'}>
              {left > 0 ? `${left} unit(s) not packed` : `${-left} unit(s) more than on hand`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RecentScans({ receiptId, events, onChange }: { receiptId: string; events: ReceiveEvent[]; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [eOutcome, setEOutcome] = useState<Outcome>('auction');
  const [eQty, setEQty] = useState('1');

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setEditing(null);
      onChange();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const undo = (e: ReceiveEvent) => {
    const reason = prompt('Why undo this scan?', 'scanned by mistake');
    if (reason) run(() => api(`/receive-events/${e.id}/void`, { body: { reason } }));
  };
  const change = (e: ReceiveEvent) =>
    run(() =>
      api(`/receive-events/${e.id}/change`, {
        body: {
          clientEventId: crypto.randomUUID(),
          productId: e.productId,
          outcome: eOutcome,
          quantity: Number(eQty),
          binId: eOutcome === 'retail' ? e.binId : null,
          packSize: eOutcome === 'retail' ? e.packSize : null,
          reason: 'changed on the spot',
        },
      }),
    );
  const reprint = (e: ReceiveEvent) =>
    run(async () => saveBlob(await apiFile('/retail/labels.pdf', { items: [{ productId: e.productId, packSize: e.packSize, copies: e.quantity }] }), `price-labels-${e.itemNumber}.pdf`));

  return (
    <section className="card">
      <h2>My recent scans</h2>
      <ErrorBox error={error} />
      {events.length === 0 && <p className="muted small">Nothing yet on this manifest.</p>}
      <ul className="recent-scans">
        {events.map((e) => (
          <li key={e.id} className={e.voidedAt ? 'voided' : ''}>
            <div>
              <strong>
                {e.quantity} × {e.packSize ? `${e.packSize}-pack ` : ''}
              </strong>
              <span className="mono">{e.itemNumber}</span> {e.title} → <strong>{OUTCOME_LABEL[e.outcome]}</strong>
              {e.binCode ? ` in ${e.binCode}` : ''}
              <div className="muted small">
                {fmtDate(e.createdAt)}
                {e.voidedAt ? ` · undone (${e.voidReason})` : ''}
                {e.replacesEventId ? ' · corrected entry' : ''}
              </div>
            </div>
            {!e.voidedAt && editing !== e.id && (
              <div className="row gap">
                <button className="btn btn-small" disabled={busy} onClick={() => undo(e)}>
                  Undo
                </button>
                <button className="btn btn-small" disabled={busy} onClick={() => (setEditing(e.id), setEOutcome(e.outcome), setEQty(String(e.quantity)))}>
                  Change
                </button>
                {e.outcome === 'retail' && (
                  <button className="btn btn-small" disabled={busy} onClick={() => reprint(e)}>
                    Reprint
                  </button>
                )}
              </div>
            )}
            {editing === e.id && (
              <div className="row gap">
                <select value={eOutcome} onChange={(x) => setEOutcome(x.target.value as Outcome)}>
                  {(['auction', 'garbage', ...(e.binId ? ['retail'] : [])] as Outcome[]).map((o) => (
                    <option key={o} value={o}>
                      {OUTCOME_LABEL[o]}
                    </option>
                  ))}
                </select>
                <input value={eQty} onChange={(x) => setEQty(x.target.value)} inputMode="numeric" style={{ width: '4rem' }} />
                <button className="btn btn-small btn-primary" disabled={busy} onClick={() => change(e)}>
                  Save
                </button>
                <button className="btn btn-small btn-ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ report
function ReportPanel({ detail, onChange }: { detail: ReceiptDetail; onChange: () => void }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const { receipt, report } = detail;
  const t = report.totals;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const differences = t.over > 0 || t.under > 0;
  const close = async () => {
    if (!confirm(`Close manifest ${receipt.receiptNumber}? It can't be changed afterwards (except by an administrator).`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/receipts/${receipt.id}/close`, { body: { note: note.trim() || undefined } });
      onChange();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm(`Delete manifest ${receipt.receiptNumber} and its ${report.items.length} expected items? Nothing has been received on it.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/receipts/${receipt.id}`, { method: 'DELETE' });
      nav('/receiving');
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const csv = async () => {
    const res = await fetch(`/api/receipts/${receipt.id}/report.csv`, { headers: { 'X-Requested-With': 'mal-app' }, credentials: 'same-origin' });
    saveBlob(await res.blob(), `manifest-${receipt.receiptNumber}.csv`);
  };
  return (
    <>
      <section className="card">
        <h2>Summary</h2>
        <div className="tiles report-tiles">
          <div className="tile">
            <span className="tile-title">{t.processed}</span>
            <span className="tile-text">of {t.expected} units</span>
          </div>
          <div className="tile">
            <span className="tile-title">{t.unitPct.retail}%</span>
            <span className="tile-text">retail ({t.retail})</span>
          </div>
          <div className="tile">
            <span className="tile-title">{t.unitPct.auction}%</span>
            <span className="tile-text">auction ({t.auction})</span>
          </div>
          <div className="tile">
            <span className="tile-title">{t.unitPct.garbage}%</span>
            <span className="tile-text">garbage ({t.garbage})</span>
          </div>
        </div>
        <p className="small">
          By value at Costco retail: retail {t.valuePct.retail}% · auction {t.valuePct.auction}% · garbage {t.valuePct.garbage}%. Manifest cost ${t.costTotal}, Costco
          retail ${t.retailTotal}.
        </p>
        <p className={differences ? 'field-error' : 'ok'}>
          {receipt.status === 'open' ? 'So far: ' : ''}
          {t.under > 0 ? `${t.under} under (Costco $${t.underValueRetail}, our cost $${t.underValueCost})` : 'no unders'} ·{' '}
          {t.over > 0 ? `${t.over} over (Costco $${t.overValueRetail})` : 'no overs'}
        </p>
        <button className="btn btn-small" onClick={csv}>
          Download CSV
        </button>
      </section>
      <section className="card">
        <h2>Items</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Item #</th>
                <th>Title</th>
                <th>Manifest</th>
                <th>Retail</th>
                <th>Auction</th>
                <th>Garbage</th>
                <th>Over / under</th>
              </tr>
            </thead>
            <tbody>
              {report.items.map((i) => (
                <tr key={i.itemNumber} className={i.over || i.under ? 'row-warn' : ''}>
                  <td className="mono">{i.itemNumber}</td>
                  <td>{i.title}</td>
                  <td>{i.expected}</td>
                  <td>{i.retail}</td>
                  <td>{i.auction}</td>
                  <td>{i.garbage}</td>
                  <td>{i.over ? `+${i.over}` : i.under ? `−${i.under}` : <Icon name="check" size={16} className="ok-text" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {receipt.status === 'open' ? (
        <section className="card">
          <h2>Close the manifest</h2>
          {differences && user?.role !== 'admin' && <p className="small">There are overs or unders, so an administrator needs to close it.</p>}
          <Field label={differences ? 'Note explaining the overs and unders (required)' : 'Note (optional)'}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </Field>
          <ErrorBox error={error} />
          <button className="btn" disabled={busy || (differences && (user?.role !== 'admin' || !note.trim()))} onClick={close}>
            Close manifest
          </button>
          {t.processed === 0 && (
            <>
              <p className="muted small">Nothing has been received yet. If this manifest was read wrongly or created by mistake, you can delete it and upload the scan again.</p>
              <button className="btn btn-danger" disabled={busy} onClick={remove}>
                Delete manifest
              </button>
            </>
          )}
        </section>
      ) : (
        <p className="muted small">
          Closed {fmtDate(receipt.closedAt!)}
          {receipt.closeNote ? `: ${receipt.closeNote}` : ''}
        </p>
      )}
    </>
  );
}

// ------------------------------------------------------------------ to list
function ToListPanel({ receiptId }: { receiptId: string }) {
  const q = useQuery({
    queryKey: ['to-list', receiptId],
    queryFn: () => api<{ items: { itemNumber: string; productId: string; title: string | null; auction: number; lotsListed: number }[] }>(`/receipts/${receiptId}/to-list`),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  return (
    <section className="card">
      <h2>To list</h2>
      <p className="muted small">Pieces sent to auction from this manifest that don't have a lot yet. Listing one opens Create lot with the product chosen.</p>
      {q.data!.items.length === 0 && <p className="ok">Everything sent to auction has been listed.</p>}
      <ul className="recent-scans">
        {q.data!.items.map((i) => (
          <li key={i.itemNumber}>
            <div>
              <span className="mono">{i.itemNumber}</span> {i.title}
              <div className="muted small">
                {i.lotsListed} of {i.auction} listed
              </div>
            </div>
            <Link className="btn btn-small btn-primary" to={`/lots/new?product=${i.productId}&receipt=${receiptId}`}>
              List one
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
